import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Upload, FileText,
  BookOpen, Pencil, Check, GripVertical, AlertTriangle, Type
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Framework {
  id: string;
  framework_name: string;
  system_prompt: string;
  quality_checklist: string | null;
}

interface Knowledge {
  id: string;
  title: string;
  content: string;
  file_name: string | null;
  file_url: string | null;
  token_estimate: number | null;
}

// Auto-save indicator
const SaveIndicator: React.FC<{ saving: boolean; saved: boolean }> = ({ saving, saved }) => {
  if (saving) return <span className="text-xs text-muted-foreground animate-pulse">Saving...</span>;
  if (saved) return <span className="text-xs text-muted-foreground flex items-center gap-1"><Check className="h-3 w-3" /> Saved</span>;
  return null;
};

const ClientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Client
  const [clientName, setClientName] = useState('');
  const [clientDescription, setClientDescription] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editingDesc, setEditingDesc] = useState(false);

  // Framework (single primary)
  const [framework, setFramework] = useState<Framework | null>(null);
  const [fwName, setFwName] = useState('');
  const [fwPrompt, setFwPrompt] = useState('');
  const [fwChecklist, setFwChecklist] = useState('');
  const [checklistOpen, setChecklistOpen] = useState(false);

  // Knowledge
  const [knowledge, setKnowledge] = useState<Knowledge[]>([]);
  const [expandedKn, setExpandedKn] = useState<string | null>(null);
  const [editingKnId, setEditingKnId] = useState<string | null>(null);
  const [editingKnContent, setEditingKnContent] = useState('');
  const [editingKnTitle, setEditingKnTitle] = useState('');

  // Add text inline
  const [addingText, setAddingText] = useState(false);
  const [newKnTitle, setNewKnTitle] = useState('');
  const [newKnContent, setNewKnContent] = useState('');

  // State
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<string | null>(null);
  const [savedField, setSavedField] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropZoneRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!id) return;
    try {
      const [clientRes, fwRes, knRes] = await Promise.all([
        supabase.from('content_lab_clients').select('name, description').eq('id', id).single(),
        supabase.from('content_lab_frameworks').select('id, framework_name, system_prompt, quality_checklist').eq('client_id', id).order('created_at', { ascending: true }).limit(1),
        supabase.from('content_lab_knowledge').select('id, title, content, file_name, file_url, token_estimate').eq('client_id', id).order('created_at', { ascending: true }),
      ]);
      if (clientRes.data) {
        setClientName(clientRes.data.name);
        setClientDescription(clientRes.data.description || '');
      }
      const fw = fwRes.data?.[0] || null;
      setFramework(fw);
      setFwName(fw?.framework_name || '');
      setFwPrompt(fw?.system_prompt || '');
      setFwChecklist(fw?.quality_checklist || '');
      setKnowledge(knRes.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- Auto-save helpers ---
  const showSaved = (field: string) => {
    setSavedField(field);
    setTimeout(() => setSavedField(null), 2000);
  };

  const saveClientField = async (field: 'name' | 'description', value: string) => {
    if (!id) return;
    setSavingField(field);
    const { error } = await supabase.from('content_lab_clients').update({ [field]: value.trim() }).eq('id', id);
    setSavingField(null);
    if (error) toast.error(error.message);
    else showSaved(field);
  };

  const saveFramework = async (updates: Partial<{ framework_name: string; system_prompt: string; quality_checklist: string | null }>, field: string) => {
    if (!id) return;
    setSavingField(field);

    if (framework) {
      const { error } = await supabase.from('content_lab_frameworks').update(updates).eq('id', framework.id);
      setSavingField(null);
      if (error) toast.error(error.message);
      else showSaved(field);
    } else {
      // Auto-create framework with "Default" name
      const insertData = {
        client_id: id,
        framework_name: updates.framework_name || fwName.trim() || 'Default',
        system_prompt: updates.system_prompt ?? fwPrompt ?? '',
        quality_checklist: updates.quality_checklist ?? fwChecklist ?? null,
      };
      const { data, error } = await supabase.from('content_lab_frameworks').insert(insertData)
        .select('id, framework_name, system_prompt, quality_checklist').single();
      setSavingField(null);
      if (error) toast.error(error.message);
      else if (data) {
        setFramework(data);
        showSaved(field);
      }
    }
  };

  // --- Knowledge ---
  const totalTokens = knowledge.reduce((sum, k) => sum + (k.token_estimate || 0), 0);

  const handleDeleteKnowledge = async (knId: string) => {
    if (!confirm('Delete this knowledge file?')) return;
    const { error } = await supabase.from('content_lab_knowledge').delete().eq('id', knId);
    if (error) toast.error(error.message);
    else {
      setKnowledge(prev => prev.filter(k => k.id !== knId));
      toast.success('Deleted');
    }
  };

  const handleAddText = async () => {
    if (!newKnTitle.trim() || !newKnContent.trim() || !id) return;
    const tokenEstimate = Math.ceil(newKnContent.length / 4);
    const { data, error } = await supabase.from('content_lab_knowledge').insert({
      client_id: id,
      title: newKnTitle.trim(),
      content: newKnContent,
      token_estimate: tokenEstimate,
    }).select('id, title, content, file_name, file_url, token_estimate').single();
    if (error) toast.error(error.message);
    else if (data) {
      setKnowledge(prev => [...prev, data]);
      setNewKnTitle('');
      setNewKnContent('');
      setAddingText(false);
      toast.success('Knowledge added');
    }
  };

  const saveKnowledgeEdit = async (knId: string) => {
    setSavingField(`kn-${knId}`);
    const tokenEstimate = Math.ceil(editingKnContent.length / 4);
    const { error } = await supabase.from('content_lab_knowledge').update({
      title: editingKnTitle.trim(),
      content: editingKnContent,
      token_estimate: tokenEstimate,
    }).eq('id', knId);
    setSavingField(null);
    if (error) toast.error(error.message);
    else {
      setKnowledge(prev => prev.map(k => k.id === knId ? { ...k, title: editingKnTitle.trim(), content: editingKnContent, token_estimate: tokenEstimate } : k));
      setEditingKnId(null);
      showSaved(`kn-${knId}`);
    }
  };

  // --- File Upload ---
  const processFiles = async (files: FileList | File[]) => {
    if (!id) return;
    const allowedTypes = ['.txt', '.md', '.pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    setUploading(true);
    for (const file of Array.from(files)) {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(ext)) {
        toast.error(`${file.name}: Only .txt, .md, .pdf files allowed`);
        continue;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: Max 5MB`);
        continue;
      }

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        const filePath = `${user.id}/${id}/${Date.now()}-${file.name}`;
        const { error: uploadError } = await supabase.storage.from('knowledge-files').upload(filePath, file);
        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage.from('knowledge-files').getPublicUrl(filePath);

        let content = '[PDF uploaded]';
        let tokenEstimate = 0;

        if (ext === '.txt' || ext === '.md') {
          content = await file.text();
          tokenEstimate = Math.ceil(content.length / 4);
        }

        const { data, error } = await supabase.from('content_lab_knowledge').insert({
          client_id: id,
          title: file.name.replace(/\.[^.]+$/, ''),
          content,
          file_name: file.name,
          file_url: urlData?.publicUrl || filePath,
          token_estimate: tokenEstimate,
        }).select('id, title, content, file_name, file_url, token_estimate').single();

        if (error) throw error;
        if (data) setKnowledge(prev => [...prev, data]);
        toast.success(`${file.name} uploaded`);
      } catch (err: any) {
        toast.error(`${file.name}: ${err.message}`);
      }
    }
    setUploading(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length) processFiles(e.dataTransfer.files);
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto space-y-10 pb-20">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/content-lab/clients')} className="text-muted-foreground -ml-2">
        <ArrowLeft className="h-4 w-4 mr-1" /> Clients
      </Button>

      {/* Section 1: Client Info */}
      <section className="space-y-2">
        {editingName ? (
          <div className="flex items-center gap-2">
            <Input
              value={clientName}
              onChange={e => setClientName(e.target.value)}
              className="text-3xl font-bold h-12 border-none shadow-none focus-visible:ring-0 px-0 bg-transparent"
              autoFocus
              onBlur={() => { setEditingName(false); saveClientField('name', clientName); }}
              onKeyDown={e => { if (e.key === 'Enter') { setEditingName(false); saveClientField('name', clientName); } }}
            />
            <SaveIndicator saving={savingField === 'name'} saved={savedField === 'name'} />
          </div>
        ) : (
          <h1
            className="text-3xl font-bold cursor-pointer hover:text-primary/80 transition-colors group"
            onClick={() => setEditingName(true)}
          >
            {clientName}
            <Pencil className="h-4 w-4 inline-block ml-2 opacity-0 group-hover:opacity-40 transition-opacity" />
          </h1>
        )}

        {editingDesc ? (
          <div className="flex items-start gap-2">
            <Textarea
              value={clientDescription}
              onChange={e => setClientDescription(e.target.value)}
              className="text-muted-foreground border-none shadow-none focus-visible:ring-0 px-0 bg-transparent resize-none min-h-[40px]"
              autoFocus
              rows={2}
              placeholder="Add a description..."
              onBlur={() => { setEditingDesc(false); saveClientField('description', clientDescription); }}
            />
            <SaveIndicator saving={savingField === 'description'} saved={savedField === 'description'} />
          </div>
        ) : (
          <p
            className="text-muted-foreground cursor-pointer hover:text-foreground/60 transition-colors"
            onClick={() => setEditingDesc(true)}
          >
            {clientDescription || 'Add a description...'}
            <Pencil className="h-3 w-3 inline-block ml-2 opacity-0 hover:opacity-40" />
          </p>
        )}
      </section>

      {/* Section 2: Instructions (Framework) */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5" /> Instructions
          </h2>
          <SaveIndicator saving={savingField === 'fw-prompt'} saved={savedField === 'fw-prompt'} />
        </div>

        {/* Framework name label */}
        <div className="flex items-center gap-2">
          <Input
            value={fwName}
            onChange={e => setFwName(e.target.value)}
            placeholder="Framework name (e.g. solution-page)"
            className="text-sm h-8 w-auto max-w-xs border-dashed bg-transparent"
            onBlur={() => {
              if (fwName.trim()) saveFramework({ framework_name: fwName.trim() }, 'fw-name');
            }}
          />
          <SaveIndicator saving={savingField === 'fw-name'} saved={savedField === 'fw-name'} />
        </div>

        {/* System prompt textarea */}
        <Textarea
          value={fwPrompt}
          onChange={e => setFwPrompt(e.target.value)}
          placeholder="Add your content framework instructions here..."
          className="font-mono text-sm min-h-[600px] leading-relaxed"
          onBlur={() => {
            if (fwPrompt.trim()) saveFramework({ system_prompt: fwPrompt }, 'fw-prompt');
          }}
        />

        {/* Quality Checklist (collapsible) */}
        <Collapsible open={checklistOpen} onOpenChange={setChecklistOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="text-muted-foreground px-0 hover:bg-transparent">
              {checklistOpen ? <ChevronDown className="h-4 w-4 mr-1" /> : <ChevronRight className="h-4 w-4 mr-1" />}
              Quality Checklist
              <SaveIndicator saving={savingField === 'fw-checklist'} saved={savedField === 'fw-checklist'} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2">
            <Textarea
              value={fwChecklist}
              onChange={e => setFwChecklist(e.target.value)}
              placeholder="Quality checklist items..."
              className="min-h-[120px] text-sm"
              onBlur={() => saveFramework({ quality_checklist: fwChecklist || null }, 'fw-checklist')}
            />
          </CollapsibleContent>
        </Collapsible>
      </section>

      {/* Section 3: Knowledge Files */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <BookOpen className="h-5 w-5" /> Knowledge
            <Badge variant={totalTokens > 150000 ? 'destructive' : 'secondary'} className="text-xs">
              ~{Math.round(totalTokens / 1000)}k tokens
            </Badge>
          </h2>
          <Button variant="outline" size="sm" onClick={() => setAddingText(true)}>
            <Type className="h-4 w-4 mr-1" /> Add text
          </Button>
        </div>

        {totalTokens > 150000 && (
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md p-3">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Total tokens exceed 150k — this may impact generation quality or hit context limits.
          </div>
        )}

        {/* Drag and drop zone */}
        <div
          ref={dropZoneRef}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50'
          }`}
        >
          <Upload className={`h-8 w-8 mx-auto mb-2 ${dragOver ? 'text-primary' : 'text-muted-foreground/50'}`} />
          <p className="text-sm text-muted-foreground">
            {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
          </p>
          <p className="text-xs text-muted-foreground/60 mt-1">.txt, .md, .pdf — max 5MB</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".txt,.md,.pdf"
            className="hidden"
            onChange={e => { if (e.target.files?.length) processFiles(e.target.files); e.target.value = ''; }}
          />
        </div>

        {/* Add text inline card */}
        {addingText && (
          <Card className="animate-fade-in">
            <CardContent className="p-4 space-y-3">
              <Input
                value={newKnTitle}
                onChange={e => setNewKnTitle(e.target.value)}
                placeholder="Title (e.g. Internal Links, Company Context)"
                autoFocus
              />
              <Textarea
                value={newKnContent}
                onChange={e => setNewKnContent(e.target.value)}
                placeholder="Paste knowledge content here..."
                className="font-mono text-sm min-h-[200px]"
              />
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleAddText} disabled={!newKnTitle.trim() || !newKnContent.trim()}>
                  <Plus className="h-4 w-4 mr-1" /> Add
                </Button>
                <Button size="sm" variant="ghost" onClick={() => { setAddingText(false); setNewKnTitle(''); setNewKnContent(''); }}>
                  Cancel
                </Button>
                {newKnContent && (
                  <span className="text-xs text-muted-foreground ml-auto">
                    ~{Math.round(Math.ceil(newKnContent.length / 4) / 1000)}k tokens
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Knowledge items */}
        {knowledge.length === 0 && !addingText ? (
          <p className="text-sm text-muted-foreground text-center py-6">No knowledge files yet. Upload files or add text above.</p>
        ) : (
          <div className="space-y-2">
            {knowledge.map(kn => (
              <Card key={kn.id} className="group">
                <CardContent className="p-3">
                  <div className="flex items-center gap-2">
                    <GripVertical className="h-4 w-4 text-muted-foreground/30 shrink-0" />
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => {
                        if (expandedKn === kn.id) {
                          setExpandedKn(null);
                          setEditingKnId(null);
                        } else {
                          setExpandedKn(kn.id);
                          setEditingKnId(null);
                          setEditingKnTitle(kn.title);
                          setEditingKnContent(kn.content);
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        {expandedKn === kn.id ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                        <span className="text-sm font-medium truncate">{kn.title}</span>
                        {kn.file_name && <span className="text-xs text-muted-foreground truncate">({kn.file_name})</span>}
                        <Badge variant="outline" className="text-xs shrink-0 ml-auto">~{Math.round((kn.token_estimate || 0) / 1000)}k</Badge>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                      onClick={() => handleDeleteKnowledge(kn.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" />
                    </Button>
                  </div>

                  {expandedKn === kn.id && (
                    <div className="mt-3 pl-6 animate-fade-in space-y-2">
                      {editingKnId === kn.id ? (
                        <>
                          <Input
                            value={editingKnTitle}
                            onChange={e => setEditingKnTitle(e.target.value)}
                            className="text-sm h-8"
                          />
                          <Textarea
                            value={editingKnContent}
                            onChange={e => setEditingKnContent(e.target.value)}
                            className="font-mono text-sm min-h-[200px]"
                          />
                          <div className="flex items-center gap-2">
                            <Button size="sm" onClick={() => saveKnowledgeEdit(kn.id)}>
                              <Check className="h-3 w-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingKnId(null)}>Cancel</Button>
                            <SaveIndicator saving={savingField === `kn-${kn.id}`} saved={savedField === `kn-${kn.id}`} />
                          </div>
                        </>
                      ) : (
                        <>
                          <pre className="whitespace-pre-wrap text-sm font-mono text-muted-foreground bg-muted/30 p-3 rounded-md max-h-[300px] overflow-y-auto leading-relaxed">
                            {kn.content}
                          </pre>
                          <Button size="sm" variant="ghost" onClick={() => setEditingKnId(kn.id)} className="text-xs">
                            <Pencil className="h-3 w-3 mr-1" /> Edit
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ClientDetail;
